import { createFileRoute, Link, useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, Check, ChevronLeft, Info, ShieldAlert } from "lucide-react";

import { Header } from "@/components/Header";
import {
  MembershipCheckoutDialog,
  type CheckoutTier,
} from "@/components/MembershipCheckoutDialog";
import { MembershipSwitchDialog } from "@/components/MembershipSwitchDialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useDevState } from "@/lib/dev-state";
import {
  isLegacySubscriber,
  legacyCatalogScopeLabel,
} from "@/lib/legacy-membership";
import { callUpdateSubscription, callRecheckSubscriptions, resolveLibraryOnlyPrice, useMetaPrices, type ResolvedMembershipPrices } from "@/lib/firebase";
import {
  findActiveSubscription,
  formatRenewalDate,
  isLibraryOnlyMember as resolveLibraryOnlyMember,
  isMembershipUpgrade,
  isSuperSubMember,
  resolveCurrentMembershipTier,
  resolveDevMembershipTier,
  resolveScheduledMembershipTier,
  resolveV2TierFromTierName,
  tierCreatorsForCheckout,
  ROSS_CREATIONS_CREATOR_ID,
  type CurrentMembershipResult,
  type ResolvedCurrentMembership,
} from "@/lib/membership-tier";
import { trackViewContent } from "@/lib/meta-pixel";
import { hrefWithSuperSubTelegramModal } from "@/lib/super-sub-telegram";

type MembershipSearch = {
  tier?: string;
  billing?: "monthly" | "yearly";
};

export const Route = createFileRoute("/membership")({
  validateSearch: (search: Record<string, unknown>): MembershipSearch => {
    const tier = typeof search.tier === "string" ? search.tier : undefined;
    const billing =
      search.billing === "monthly" || search.billing === "yearly"
        ? search.billing
        : undefined;
    return { tier, billing };
  },
  head: () => ({
    meta: [
      { title: "Membership — Real Pranks" },
      {
        name: "description",
        content: "Unlock unlimited prank videos with a Real Pranks membership.",
      },
      { property: "og:title", content: "Membership — Real Pranks" },
      {
        property: "og:description",
        content: "Unlock unlimited prank videos with a Real Pranks membership.",
      },
    ],
  }),
  component: MembershipPage,
});

/** Split USD like the legacy plan cards: large dollars + `.cents` column + suffix. */
function splitUsdParts(amount: number): { dollars: string; cents: string } {
  const parts = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).formatToParts(amount);
  const currency = parts.find((p) => p.type === "currency")?.value ?? "$";
  const integer = parts.find((p) => p.type === "integer")?.value ?? "0";
  const fraction = parts.find((p) => p.type === "fraction")?.value ?? "00";
  return { dollars: `${currency}${integer}`, cents: fraction };
}

type Billing = "monthly" | "yearly";

const tierActionButtonClass = (tierName: string) =>
  tierName === "Basic"
    ? "mt-1 w-full rounded-lg border border-border bg-white py-6 text-base font-medium text-black shadow-none hover:bg-white/90"
    : "mt-1 w-full rounded-lg border border-white bg-yellow-400 py-6 text-base font-bold text-black shadow-none hover:bg-yellow-500";

type TierTemplate = {
  name: string;
  /** Price key when billing=monthly. */
  monthlyKey: keyof ResolvedMembershipPrices;
  /** Price key when billing=yearly. If omitted, tier ignores the toggle. */
  yearlyKey?: keyof ResolvedMembershipPrices;
  features: React.ReactNode[];
  highlight: boolean;
  badge?: string;
  /** Yellow title styling (Plus / Super Sub). */
  accentTitle?: boolean;
  /** Crown emoji after the title (Super Sub). */
  crown?: boolean;
};

const tierTemplates: TierTemplate[] = [
  {
    name: "Basic",
    monthlyKey: "monthly",
    yearlyKey: "yearly",
    features: [
      "Watch 3 New Videos right now",
      "1 New RossCreations video every week",
      "Chat with creators and other fans in comments",
    ],
    highlight: false,
  },
  {
    name: "Plus",
    monthlyKey: "plusMonthly",
    yearlyKey: "plusYearly",
    features: [
      "Watch 3 New Videos right now",
      "1 New RossCreations video every week",
      "Bonus video each week from an additional creator or from RossCreations",
      "Chat with creators and other fans in comments",
    ],
    highlight: true,
    accentTitle: true,
  },
  {
    name: "Super Sub",
    monthlyKey: "superSub",
    yearlyKey: "superSubYearly",
    features: [
      "Everything in Plus",
      "VIP Group chat with Ross and other Super Subs on Telegram",
      "Super Sub badge in the comments",
    ],
    highlight: false,
    badge: "VIP",
    accentTitle: true,
    crown: true,
  },
];

function MembershipPage() {
  const navigate = useNavigate();
  const router = useRouter();
  const search = Route.useSearch();
  const { signedIn, loading: authLoading, isSubscriber, subscriptions, membershipType, creators } = useAuth();
  const dev = useDevState();
  const { ready, resolution, doc: pricesDoc } = useMetaPrices();
  const pricesOk = resolution?.ok === true;
  const blockMessage = ready && resolution && !resolution.ok ? resolution.message : null;
  const libraryOnlyPriceUsd = resolveLibraryOnlyPrice(pricesDoc);

  const [billing, setBilling] = useState<Billing>(search.billing ?? "monthly");
  const [switchTier, setSwitchTier] = useState<CheckoutTier | null>(null);
  const [switchSubmitting, setSwitchSubmitting] = useState(false);
  const [showBack, setShowBack] = useState(false);
  const billingSyncedRef = useRef(false);

  // Snapshot once on mount so in-page checkout navigations (?tier=) don't toggle visibility.
  useEffect(() => {
    setShowBack(router.history.canGoBack());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeSubscription = useMemo(
    () => findActiveSubscription(subscriptions),
    [subscriptions],
  );

  const isLibraryOnlyMember = resolveLibraryOnlyMember(subscriptions, {
    enabled: dev.state !== "real" && dev.isSubscribed,
    tier: dev.tier,
  });

  const resolvedTier = useMemo((): CurrentMembershipResult => {
    if (!pricesOk || !resolution) return null;
    if (dev.state !== "real" && dev.isSubscribed) {
      if (dev.membershipType === "legacy") {
        return {
          isLegacy: true,
          subscriptionId: "dev-preview",
          referralCreator: ROSS_CREATIONS_CREATOR_ID,
          plan: "one-creator",
        };
      }
      return resolveDevMembershipTier(dev.tier, "monthly", resolution);
    }
    return resolveCurrentMembershipTier(activeSubscription, resolution);
  }, [
    pricesOk,
    resolution,
    dev.state,
    dev.isSubscribed,
    dev.membershipType,
    dev.tier,
    activeSubscription,
  ]);

  const currentMembership = useMemo((): ResolvedCurrentMembership | null => {
    if (!resolvedTier || resolvedTier.isLegacy) return null;
    return resolvedTier;
  }, [resolvedTier]);

  const isLegacyMembership = useMemo(
    () =>
      isLegacySubscriber({
        membershipType,
        currentMembership: resolvedTier,
        devMembershipType: dev.membershipType,
        isDevMode: dev.state !== "real",
      }),
    [membershipType, resolvedTier, dev.membershipType, dev.state],
  );

  const legacySwitchContext = useMemo(() => {
    if (resolvedTier?.isLegacy) {
      return {
        subscriptionId: resolvedTier.subscriptionId,
        referralCreator: resolvedTier.referralCreator,
      };
    }
    if (membershipType === "legacy" && activeSubscription) {
      return {
        subscriptionId: activeSubscription.id,
        referralCreator:
          activeSubscription.referralCreator ?? ROSS_CREATIONS_CREATOR_ID,
      };
    }
    return null;
  }, [resolvedTier, membershipType, activeSubscription]);

  const planSwitchContext = currentMembership ?? legacySwitchContext;

  const scheduledTier = useMemo(() => {
    if (!pricesOk || !resolution) return null;
    return resolveScheduledMembershipTier(activeSubscription, resolution);
  }, [pricesOk, resolution, activeSubscription]);

  const scheduledRenewalDate = useMemo(
    () => formatRenewalDate(activeSubscription?.renewsAt),
    [activeSubscription],
  );

  const superSubAccess = isSuperSubMember(
    subscriptions,
    dev.state === "real" ? undefined : dev.tier,
  );

  const showSubscriberUi = isSubscriber || (dev.state !== "real" && dev.isSubscribed);

  useEffect(() => {
    if (!ready || showSubscriberUi) return;
    trackViewContent({ content_name: "Membership" });
  }, [ready, showSubscriberUi]);

  // Sync billing toggle to the user's current plan once prices are ready.
  useEffect(() => {
    if (!currentMembership || billingSyncedRef.current) return;
    setBilling(currentMembership.billing);
    billingSyncedRef.current = true;
  }, [currentMembership]);

  // Keep the monthly/yearly toggle in sync when arriving via deep link.
  useEffect(() => {
    if (search.billing && search.billing !== billing) setBilling(search.billing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.billing]);

  // Subscribed users should not open checkout via ?tier= deep links.
  useEffect(() => {
    if (!search.tier || !showSubscriberUi) return;
    navigate({ to: "/membership", search: {}, replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.tier, showSubscriberUi]);

  const checkoutTier = useMemo<CheckoutTier | null>(() => {
    if (!search.tier || !ready || !pricesOk || showSubscriberUi) return null;
    const template = tierTemplates.find(
      (t) => t.name.toLowerCase() === search.tier!.toLowerCase(),
    );
    if (!template) return null;
    const useYearly = search.billing === "yearly" && !!template.yearlyKey;
    const key = useYearly
      ? (template.yearlyKey as keyof ResolvedMembershipPrices)
      : template.monthlyKey;
    const price = resolution.prices[key];
    if (price == null) return null;
    return {
      name: template.name,
      planId: resolution.planIds[key] ?? null,
      price,
      billing: useYearly ? "yearly" : "monthly",
    };
  }, [search.tier, search.billing, ready, pricesOk, resolution, showSubscriberUi]);

  const closeCheckout = () => {
    navigate({ to: "/membership", search: {}, replace: true });
  };

  const handleSubscribe = (tier: CheckoutTier) => {
    if (!pricesOk) {
      toast.error("Subscription prices aren't available. You can't sign up right now.");
      return;
    }
    if (!signedIn) {
      navigate({ to: "/signup", search: { redirect: "/membership" } });
      return;
    }
    if (!tier.planId) {
      toast.error("This membership is missing a payment plan id.");
      return;
    }
    navigate({
      to: "/membership",
      search: {
        tier: tier.name.toLowerCase(),
        billing: tier.billing ?? "monthly",
      },
      resetScroll: false,
    });
  };

  const handleSwitchClick = (tier: CheckoutTier) => {
    if (!pricesOk) {
      toast.error("Subscription prices aren't available right now.");
      return;
    }
    if (!tier.planId) {
      toast.error("This membership is missing a payment plan id.");
      return;
    }
    if (!planSwitchContext) {
      toast.error("We couldn't find your current subscription.");
      return;
    }
    setSwitchTier(tier);
  };

  const handleConfirmSwitch = async () => {
    if (!switchTier?.planId || !planSwitchContext) return;

    if (planSwitchContext.subscriptionId === "dev-preview") {
      toast.info("Dev preview: plan switch is not sent to the backend.");
      setSwitchTier(null);
      return;
    }

    setSwitchSubmitting(true);
    const v2Tier = resolveV2TierFromTierName(switchTier.name);
    const result = await callUpdateSubscription({
      subscriptionId: planSwitchContext.subscriptionId,
      creators: tierCreatorsForCheckout(switchTier.name),
      referralCreator: planSwitchContext.referralCreator,
      plan: switchTier.planId,
      ...(v2Tier ? { v2Tier } : {}),
    });
    setSwitchSubmitting(false);

    if ("error" in result) {
      toast.error(result.error);
      return;
    }

    void callRecheckSubscriptions().then((recheck) => {
      if ("error" in recheck) {
        console.warn("recheckSubscriptions after plan switch:", recheck.error);
      }
    });

    toast.success(
      switchIsUpgrade
        ? result.message
        : `${result.message} Your new plan starts at your next billing date.`,
    );
    setSwitchTier(null);
    if (v2Tier === "super-sub") {
      router.history.push(hrefWithSuperSubTelegramModal("/membership"));
    }
  };

  const switchIsUpgrade =
    currentMembership && switchTier && !isLegacyMembership
      ? isMembershipUpgrade(
          currentMembership,
          { tierName: switchTier.name as ResolvedCurrentMembership["tierName"], price: switchTier.price },
          activeSubscription?.renewPrice ?? switchTier.price,
        )
      : true;

  const legacyScopeLabel = legacyCatalogScopeLabel(creators);

  // Deep links while signed out still funnel into the signup flow first.
  useEffect(() => {
    if (!search.tier || authLoading || signedIn) return;
    navigate({ to: "/signup", search: { redirect: "/membership" } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.tier, authLoading, signedIn]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-6xl px-4 py-8 md:py-10">
        {showBack ? (
          <button
            type="button"
            onClick={() => router.history.back()}
            className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </button>
        ) : null}
        <h1 className="text-xl font-bold tracking-tight md:text-2xl">
          {showSubscriberUi ? "Your membership" : "Select a membership option"}
        </h1>

        {signedIn ? (
          <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              {showSubscriberUi
                ? "Switch plans anytime. Upgrades apply immediately; downgrades take effect at your next billing date."
                : "Select a membership to immediately start viewing content"}
            </p>
            <Button asChild variant="secondary" size="sm" className="w-fit rounded-lg shrink-0">
              <Link to="/account">Account &amp; billing</Link>
            </Button>
          </div>
        ) : (
          <>
            <p className="mt-2 text-sm text-muted-foreground">
              Sign in or subscribe to manage your membership.
            </p>
            <div className="pt-2 pb-2">
              <Button variant="ghost" className="h-auto px-0 text-foreground" asChild>
                <Link to="/login" search={{ redirect: "/membership" }}>
                  Already have an account?
                </Link>
              </Button>
            </div>
          </>
        )}

        {isLegacyMembership && (
          <Alert className="mx-auto mt-6 max-w-2xl border-[oklch(0.62_0.19_250)]/40 bg-[oklch(0.62_0.19_250)]/5">
            <ShieldAlert className="h-4 w-4" />
            <AlertTitle>OG Legacy subscriber</AlertTitle>
            <AlertDescription className="space-y-2">
              <p>
                You&apos;re on an <strong>OG Legacy membership</strong> with full access to{" "}
                {legacyScopeLabel}. This catalog stays available only while you remain on
                your OG Legacy plan.
              </p>
              <p>
                Switching to a new membership below moves you to the{" "}
                <strong>weekly release model</strong>: 3 starter videos plus one new video
                each week. Your full OG Legacy catalog does not carry over.
              </p>
              <p className="text-foreground/80">
                If you cancel without switching, you&apos;ll lose access to your entire
                library. Resubscribing later starts fresh on the weekly drip model.
              </p>
            </AlertDescription>
          </Alert>
        )}

        {scheduledTier && scheduledRenewalDate && (
          <Alert className="mx-auto mt-6 max-w-2xl">
            <Info className="h-4 w-4" />
            <AlertTitle>Upcoming plan change</AlertTitle>
            <AlertDescription>
              Your plan will change to{" "}
              <strong>
                {scheduledTier.tierName}
                {scheduledTier.tierName === "Library Only"
                  ? ` ($${libraryOnlyPriceUsd.toFixed(2)}/month, no new weekly drops)`
                  : scheduledTier.billing === "yearly"
                    ? " (yearly)"
                    : " (monthly)"}
              </strong>{" "}
              on <strong>{scheduledRenewalDate}</strong>.
            </AlertDescription>
          </Alert>
        )}

        {blockMessage && (
          <Alert variant="destructive" className="mx-auto mt-6 max-w-2xl">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{signedIn ? "Pricing unavailable" : "Sign-up unavailable"}</AlertTitle>
            <AlertDescription>{blockMessage}</AlertDescription>
          </Alert>
        )}

        {superSubAccess ? (
          <div className="mx-auto mt-6 flex max-w-2xl justify-center">
            <Button asChild size="lg">
              <Link to="/vip-chat">Open VIP Chat</Link>
            </Button>
          </div>
        ) : null}

        <div className="mt-6 flex justify-center">
          <div className="inline-flex items-center rounded-full border border-border bg-card p-1">
            {(["monthly", "yearly"] as const).map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => setBilling(opt)}
                className={`relative rounded-full px-5 py-2 text-sm font-semibold capitalize transition-colors ${
                  billing === opt
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {opt}
                {opt === "yearly" && (
                  <span
                    className={`ml-2 rounded-full px-2 py-0.5 text-[10px] font-bold ${
                      billing === "yearly"
                        ? "bg-primary-foreground/20 text-primary-foreground"
                        : "bg-primary/15 text-primary"
                    }`}
                  >
                    SAVE 20%
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {showSubscriberUi && isLibraryOnlyMember && (
          <Alert className="mx-auto mt-6 max-w-2xl border-[oklch(0.62_0.19_250)]/40 bg-[oklch(0.62_0.19_250)]/5">
            <Info className="h-4 w-4" />
            <AlertTitle>Library Only plan</AlertTitle>
            <AlertDescription>
              You&apos;re on the <strong>Library Only</strong> plan for{" "}
              <strong>${libraryOnlyPriceUsd.toFixed(2)}/month</strong>. You keep the
              videos already in your library. New weekly drops are not included. Choose a
              plan below to start receiving them.
            </AlertDescription>
          </Alert>
        )}

        <div className="mt-8 flex min-h-[28rem] flex-col gap-4 md:flex-row md:gap-4">
          {tierTemplates.map((t) => {
            const ribbon = t.badge ?? (t.highlight ? "BEST VALUE" : null);
            const useYearly = billing === "yearly" && !!t.yearlyKey;
            const priceKey = useYearly ? (t.yearlyKey as keyof ResolvedMembershipPrices) : t.monthlyKey;
            const yearlyTotal = pricesOk && t.yearlyKey ? resolution.prices[t.yearlyKey] : null;
            const rawAmount = pricesOk ? resolution.prices[priceKey] : null;
            const planId = pricesOk ? (resolution.planIds[priceKey] ?? null) : null;
            const displayAmount =
              rawAmount != null && useYearly ? rawAmount / 12 : rawAmount;
            const priceSuffix = "Monthly";
            const parts = displayAmount != null ? splitUsdParts(displayAmount) : null;
            const tier: CheckoutTier | null =
              rawAmount != null
                ? { name: t.name, planId, price: rawAmount, billing: useYearly ? "yearly" : "monthly" }
                : null;

            const isCurrentPlan =
              !!currentMembership &&
              currentMembership.tierName === t.name &&
              currentMembership.billing === (useYearly ? "yearly" : "monthly");

            return (
              <div
                key={t.name}
                className={`relative flex min-w-0 flex-1 flex-col rounded-lg border border-border bg-card px-4 py-3 text-card-foreground ${
                  isCurrentPlan
                    ? "ring-2 ring-primary/40"
                    : t.highlight
                      ? "shadow-md ring-1 ring-primary/20"
                      : ""
                }`}
              >
                {isCurrentPlan ? (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[oklch(0.62_0.19_250)] px-3 py-1 text-xs font-bold text-white">
                    Your current plan
                  </span>
                ) : (
                  ribbon && (
                    <span
                      className={`absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-1 text-xs font-bold ${
                        ribbon === "VIP"
                          ? "bg-yellow-400 text-black"
                          : "bg-primary text-primary-foreground"
                      }`}
                    >
                      {ribbon}
                    </span>
                  )
                )}
                <h2
                  className={`flex flex-wrap items-center gap-2 pb-1 text-[1.1rem] font-bold ${
                    t.accentTitle ? "text-yellow-400" : ""
                  }`}
                >
                  {t.name}
                  {t.crown && <span>👑</span>}
                </h2>

                <div className="relative flex items-center gap-1 pb-2">
                  {!ready ? (
                    <span
                      className="inline-block h-12 w-32 animate-pulse rounded-md bg-muted"
                      aria-hidden
                    />
                  ) : parts ? (
                    <>
                      <p className="sr-only">
                        {displayAmount} dollars {priceSuffix.toLowerCase()}
                      </p>
                      <p className="m-0 flex items-center gap-1" aria-hidden>
                        <span className="text-4xl font-bold tabular-nums tracking-tight">
                          {parts.dollars}
                        </span>
                        <span className="flex flex-col leading-tight">
                          <span className="text-sm font-bold">.{parts.cents}</span>
                          <span className="text-xs font-medium text-muted-foreground">
                            {priceSuffix}
                          </span>
                        </span>
                      </p>
                    </>
                  ) : (
                    <span className="text-2xl font-bold text-muted-foreground">—</span>
                  )}
                </div>
                {useYearly && yearlyTotal != null && (
                  <p className="-mt-1 pb-2 text-xs text-muted-foreground">
                    Billed ${yearlyTotal.toLocaleString()} per year
                  </p>
                )}

                {isCurrentPlan ? (
                  <Button
                    type="button"
                    className="mt-1 w-full rounded-lg py-6 text-base font-medium shadow-none"
                    variant="outline"
                    disabled
                  >
                    Current plan
                  </Button>
                ) : showSubscriberUi && isLegacyMembership ? (
                  <Button
                    type="button"
                    className={tierActionButtonClass(t.name)}
                    variant="secondary"
                    disabled={!ready || !pricesOk || !tier || !tier.planId || !planSwitchContext}
                    onClick={() => tier && handleSwitchClick(tier)}
                  >
                    Switch to new membership
                  </Button>
                ) : showSubscriberUi && currentMembership ? (
                  <Button
                    type="button"
                    className={tierActionButtonClass(t.name)}
                    variant="secondary"
                    disabled={!ready || !pricesOk || !tier || !tier.planId}
                    onClick={() => tier && handleSwitchClick(tier)}
                  >
                    Switch to this membership
                  </Button>
                ) : (
                  <Button
                    type="button"
                    className={tierActionButtonClass(t.name)}
                    variant="secondary"
                    disabled={!ready || !pricesOk || !tier || !tier.planId}
                    onClick={() => tier && handleSubscribe(tier)}
                  >
                    Subscribe
                  </Button>
                )}

                <p className="m-0 pt-3 text-sm font-bold">Plan Includes:</p>
                <ul className="m-0 flex list-none flex-col gap-1 py-2 pl-0 text-[0.95rem]">
                  {t.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="mt-0.5 h-[1.2rem] w-[1.2rem] shrink-0 text-primary" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <MembershipCheckoutDialog
          tier={checkoutTier}
          open={!!checkoutTier && signedIn && !showSubscriberUi}
          onOpenChange={(open) => {
            if (!open) closeCheckout();
          }}
        />

        <MembershipSwitchDialog
          tier={switchTier}
          open={!!switchTier}
          onOpenChange={(open) => {
            if (!open && !switchSubmitting) setSwitchTier(null);
          }}
          subscriptionId={planSwitchContext?.subscriptionId ?? null}
          referralCreator={planSwitchContext?.referralCreator ?? null}
          isUpgrade={switchIsUpgrade}
          isLegacyMigration={isLegacyMembership}
          submitting={switchSubmitting}
          onConfirm={handleConfirmSwitch}
        />
      </main>
    </div>
  );
}
